import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { DynamicPricingService } from '@app/features/pricing-and-availability/services/dynamic-pricing/dynamic-pricing.service';
import { DynamicPricingActivateModalComponent } from '@app/modules/calendar/components/dynamic-pricing-activate-modal/dynamic-pricing-activate-modal.component';
import { DynamicPricingDeActivateModalComponent } from '@app/modules/calendar/components/dynamic-pricing-de-activate-modal/dynamic-pricing-de-activate-modal.component';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import {
  DialogData,
  DYNAMIC_PRICING_ACTIVATE_PERMISSIONS,
  DynamicPricingOptionsPayload,
  DynamicPricingOptionsResponse,
  PricingPreference,
} from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { FunctionPipe } from '@app/shared/pipes/function/function.pipe';
import { EventsService } from '@app/shared/services/events/events.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { UiModule } from '@app/ui';
import { MaterialModule } from '@app/ui/material.module';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs';

type ViewStep = 'dynamic-pricing-preview' | 'dynamic-pricing-settings';
// todo dynamic events list

interface DynamicPricingFormType {
  pricingPreference: FormControl<PricingPreference>;
  basePrice: FormControl<number | null>;
  minPrice: FormControl<number | null>;
  maxPrice: FormControl<number | null>;
}

const minLessThanMaxValidator = (group: AbstractControl): ValidationErrors | null => {
  const min = group.get('minPrice')?.value;
  const max = group.get('maxPrice')?.value;

  if (min != null && max != null && min > max) {
    return { minGreaterThanMax: true };
  }
  return null;
};

@Component({
  standalone: true,
  selector: 'sbnb-sidebar-property-dynamic-pricing',
  templateUrl: './sidebar-property-dynamic-pricing.component.html',
  styles: `
    @import 'variables';
    .padding-children > * {
      padding-left: 16px;
      padding-right: 16px;
    }

    ::ng-deep .mat-form-field-wrapper {
      padding: 0px !important;
    }

    .sticky-footer {
      margin: auto;
      width: 100%;
      border-top: 1px solid $smoke;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px;
      background: $white;
      position: sticky;
      bottom: 0;
    }
  `,
  imports: [
    CommonModule,
    UiModule,
    DynamicPricingActivateModalComponent,
    DynamicPricingDeActivateModalComponent,
    MaterialModule,
    FunctionPipe,
    ReactiveFormsModule,
  ],
})
export class SidebarPropertyDynamicPricingComponent extends OnDestroyComponent implements OnInit {
  @Input() property: any;
  @Output() hide = new EventEmitter<void>();

  public step: ViewStep = 'dynamic-pricing-preview';
  public mode: 'setup' | 'update' = 'setup';
  public manualPricedDaysPercentage?: number;
  public PRICING_PREFERENCE = PricingPreference;

  public minPrice?: number;
  public maxPrice?: number;

  public pricingPreferences: { label: string; value: PricingPreference; percentageDifference: string }[] = [
    {
      label: 'More conservative',
      value: PricingPreference.CONSERVATIVE,
      percentageDifference: '-10',
    },
    {
      label: 'Recommended',
      value: PricingPreference.RECOMMENDED,
      percentageDifference: '',
    },
    {
      label: 'More aggressive',
      value: PricingPreference.AGGRESSIVE,
      percentageDifference: '+10',
    },
  ];

  displayPrices: DynamicPricingOptionsResponse['base_prices'] = {
    conservative: { amount: 0, display: '' },
    recommended: { amount: 0, display: '' },
    aggressive: { amount: 0, display: '' },
    custom: { amount: 0, display: '' },
  };
  displayPrices$ = new BehaviorSubject<DynamicPricingOptionsResponse['base_prices']>(this.displayPrices);

  basePrices?: DynamicPricingOptionsResponse['base_prices'];

  dynamicPricingForm = this.formBuilder.group<DynamicPricingFormType>(
    {
      pricingPreference: new FormControl(PricingPreference.RECOMMENDED, [Validators.required]),
      basePrice: new FormControl(null, [Validators.min(0)]),
      minPrice: new FormControl(null, [Validators.min(0)]),
      maxPrice: new FormControl(null, [Validators.min(0)]),
    },
    { validators: [minLessThanMaxValidator] }
  );

  public updateLoading = false;
  public $dynamicPricingOptions = this.dynamicPricingService.$config;
  public $dynamicPricingOn = this.dynamicPricingService.$dynamicPricingOn;
  public dynamicPricingActivatePermissions$ = this.dynamicPricingService.dynamicPricingActivatePermissions$;
  public DYNAMIC_PRICING_ACTIVATE_PERMISSIONS = DYNAMIC_PRICING_ACTIVATE_PERMISSIONS;

  private dynamicPricingPusherUpdates$ = this.dynamicPricingService.dynamicPricingPusherUpdates$;
  constructor(
    private dialog: MatDialog,
    private eventsService: EventsService,
    private propertiesService: PropertiesService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private router: Router,
    private dynamicPricingService: DynamicPricingService,
    private segmentIoService: SegmentIoService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.$dynamicPricingOn()) {
      this.step = 'dynamic-pricing-settings';
      this.mode = 'update';
      const { current_base_price_type, base_prices, min_price, max_price } = this.$dynamicPricingOptions();
      this.dynamicPricingForm.patchValue({
        pricingPreference: current_base_price_type,
        basePrice: base_prices?.custom?.amount ?? null,
        minPrice: min_price != null ? Number(min_price?.amount) : null,
        maxPrice: max_price != null ? Number(max_price?.amount) : null,
      });
      this.fetchDynamicPricingPreview();
    } else {
      // mode is 'setup' in this case, prepare manualPricedDaysPercentage, probably seasonality note too
      this.propertiesService.fetchFutureManualPricedDaysPercentage(this.property.id, 24).then((percentage) => {
        this.manualPricedDaysPercentage = percentage;
      });
      // nothing saved, base fetch
      this.eventsService.fire(`dynamic-pricing-preview-fetch-${this.property.id}`, {
        base_price_type: this.$dynamicPricingOptions()?.current_base_price_type ?? 'recommended',
      });
    }

    this.dynamicPricingPusherUpdates$
      .pipe(
        takeUntil(this.destroyed$),
        filter((updated) => Boolean(updated))
      )
      .subscribe(() => {
        this.updateLoading = false;
        this.cdr.detectChanges();
      });

    /*
    Subscribe to custom price changes, to ensure the pricing preview is updated when the custom price is changed
    In general, this should follow min_price/max_price changes areas of interest (default, update, part of update request, part of activate modal options)
    */
    // custom preview && loading

    this.dynamicPricingForm
      .get('basePrice')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(1600), distinctUntilChanged())
      .subscribe(() => {
        this.fetchDynamicPricingPreview();
      });

    this.dynamicPricingForm
      .get('basePrice')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.eventsService.fire(`dynamic-pricing-preview-loading-${this.property.id}`, true);
      });

    // min price preview && loading
    this.dynamicPricingForm
      .get('minPrice')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(1600), distinctUntilChanged())
      .subscribe(() => {
        this.fetchDynamicPricingPreview();
      });

    this.dynamicPricingForm
      .get('minPrice')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.eventsService.fire(`dynamic-pricing-preview-loading-${this.property.id}`, true);
      });

    // max price preview && loading
    this.dynamicPricingForm
      .get('maxPrice')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(1600), distinctUntilChanged())
      .subscribe(() => {
        this.fetchDynamicPricingPreview();
      });

    this.dynamicPricingForm
      .get('maxPrice')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(() => {
        this.eventsService.fire(`dynamic-pricing-preview-loading-${this.property.id}`, true);
      });
  }

  public presentDynamicPricingModal() {
    this.segmentIoService.track(SegmentEvent.DynamicPricingContinueToActivation, {
      property_id: this.property.id,
    });
    const data: DialogData = {
      mode: this.mode,
      property: this.property,
      dynamic_pricing_enabled: true,
      manualPricedDaysPercentage: this.manualPricedDaysPercentage,
      base_price_type: this.dynamicPricingForm.value.pricingPreference,
      base_price:
        this.dynamicPricingForm.value.pricingPreference === PricingPreference.CUSTOM &&
        this.dynamicPricingForm.value.basePrice != null
          ? Number(this.dynamicPricingForm.value.basePrice)
          : undefined,
      min_price:
        this.dynamicPricingForm.value.minPrice != null ? Number(this.dynamicPricingForm.value.minPrice) : undefined,
      max_price:
        this.dynamicPricingForm.value.maxPrice != null ? Number(this.dynamicPricingForm.value.maxPrice) : undefined,
    };
    const dialogRef = this.dialog.open(DynamicPricingActivateModalComponent, {
      disableClose: true,
      height: '100vh',
      width: '100vw',
      maxWidth: '100%',
      maxHeight: '100%',
      data,
    });
  }

  public setPricingPreference(preference: PricingPreference, fetchPreview = true) {
    this.dynamicPricingForm.patchValue({
      pricingPreference: preference,
    });
    this.dynamicPricingForm.markAsDirty();
    if (fetchPreview) {
      this.fetchDynamicPricingPreview();
    }

    this.segmentIoService.track(SegmentEvent.DynamicPricingChangeStrategy, {
      property_id: this.property.id,
      strategy: preference,
      dynamic_pricing_enabled: this.$dynamicPricingOn(),
    });
  }

  private fetchDynamicPricingPreview(): void {
    this.eventsService.fire(`dynamic-pricing-preview-fetch-${this.property.id}`, {
      base_price:
        this.dynamicPricingForm.value.pricingPreference === PricingPreference.CUSTOM &&
        this.dynamicPricingForm.value.basePrice != null
          ? Number(this.dynamicPricingForm.value.basePrice)
          : undefined,
      min_price:
        this.dynamicPricingForm.value.minPrice != null ? Number(this.dynamicPricingForm.value.minPrice) : undefined,
      max_price:
        this.dynamicPricingForm.value.maxPrice != null ? Number(this.dynamicPricingForm.value.maxPrice) : undefined,
      base_price_type: this.dynamicPricingForm.value.pricingPreference,
    });
  }

  public setStep(step: ViewStep, errors: any) {
    if (errors && this.hasErrors(errors)) {
      return;
    }
    this.step = step;
    if (step === 'dynamic-pricing-settings') {
      this.segmentIoService.track(SegmentEvent.DynamicPricingCustomizeSetup, {
        property_id: this.property.id,
      });
      this.propertiesService
        .getDynamicPricingOptions(this.property.id)
        .subscribe((response: DynamicPricingOptionsResponse) => {
          this.displayPrices$.next(response.base_prices);
          this.cdr.detectChanges();
        });
    }
  }

  public getBasePriceByPreference(prices: any[], preference: PricingPreference) {
    return prices.find((price) => price[preference] !== null)?.[preference]?.display;
  }

  public presentDynamicPricingDeActivateModal() {
    const dialogRef = this.dialog.open(DynamicPricingDeActivateModalComponent, {
      disableClose: true,
      height: '100vh',
      width: '100vw',
      maxWidth: '100%',
      maxHeight: '100%',
      data: {
        property: this.property,
      },
    });
  }

  public updateDynamicPricing() {
    this.updateLoading = true;

    const payload: DynamicPricingOptionsPayload = {
      base_price_type:
        this.dynamicPricingForm.value.pricingPreference ?? this.$dynamicPricingOptions()?.current_base_price_type,
      dynamic_pricing_enabled: true,
      outbound_dynamic_pricing_enabled: true,
      override_manual_edits: this.$dynamicPricingOptions()?.override_manual_edits,
      base_price:
        this.dynamicPricingForm.value.pricingPreference === PricingPreference.CUSTOM &&
        this.dynamicPricingForm.value.basePrice != null
          ? Number(this.dynamicPricingForm.value.basePrice)
          : undefined,
      min_price:
        this.dynamicPricingForm.value.minPrice != null ? Number(this.dynamicPricingForm.value.minPrice) : undefined,
      max_price:
        this.dynamicPricingForm.value.maxPrice != null ? Number(this.dynamicPricingForm.value.maxPrice) : undefined,
    };

    this.propertiesService.setDynamicPricingOptions(this.property.id, payload).subscribe({
      next: (resp) => {
        this.dynamicPricingService.setDynamicPricingConfig({
          ...(resp as DynamicPricingOptionsResponse),
          current_base_price_type: payload.base_price_type,
        });
        // updateLoading is not set to false until the pusher event is received
        setTimeout(() => {
          this.eventsService.fire(`dynamic-pricing-updated-${this.property.id}`, true);
          this.dynamicPricingForm.markAsPristine();
          this.cdr.detectChanges();
        }, 5000);
      },
      error: () => {
        this.updateLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  visitBilling() {
    this.router.navigate(['/settings/your-plan']);
  }

  hasErrors(value: object | null) {
    return value != null && Object.keys(value).length > 0;
  }

  goBack() {
    this.dynamicPricingService.toggleVisibility(false);
    this.hide.emit();
  }
}
